import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../../../components/loading/Loader';
import { Panel } from '../../../components/panels/Panel';
import { groupVeralliaDashboardDetections } from '../../../services/dashboard/verallia-dashboard-grouping-service';
import {
  filterVeralliaDashboardDetections,
  getVeralliaDashboardByDay,
  getVeralliaDashboardByHour,
  getVeralliaDashboardByLine,
  getVeralliaDashboardByMonth,
  getVeralliaDashboardByShift,
  getVeralliaDashboardByWeek,
  getVeralliaDashboardDefectClassification,
  getVeralliaDashboardLinesReporting,
  getVeralliaDashboardShift,
  sortVeralliaDashboardDetections,
} from '../../../services/dashboard/verallia-dashboard-service';
import { VeralliaDashboardFilters } from './components/VeralliaDashboardFilters';
import { VeralliaDashboardKpi, VeralliaDashboardNoData } from './components/VeralliaDashboardBlocks';
import {
  graphColors,
  VeralliaDashboardBarChart,
  VeralliaDashboardDefectClassification,
} from './components/VeralliaDashboardCharts';

const statisticsDateFormatter = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' });

const getStatisticsDateParts = (timestamp) => Object.fromEntries(statisticsDateFormatter.formatToParts(new Date(timestamp * 1000)).filter(({ type }) => type !== 'literal').map(({ type, value }) => [type, Number(value)]));

const getStatisticsIsoWeek = ({ year, month, day }) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - start) / 86400000 + 1) / 7);
};

const matchesStatisticsBar = (detection, category, index, label) => {
  if (category === 'line') return detection.line === label;
  if (category === 'shift') return getVeralliaDashboardShift(detection.timestamp) === label;
  const parts = getStatisticsDateParts(detection.timestamp);
  if (category === 'hour') return parts.hour === index;
  if (category === 'day') return parts.day === index + 1;
  if (category === 'week') return `W${getStatisticsIsoWeek(parts)}` === label;
  if (category === 'month') return parts.month === index + 1;
  return false;
};

export const VeralliaDashboardStatistics = ({ detections, isInfluxLoading, hasInfluxError, filters, onFiltersChange, onDetectionsPageChange }) => {
  const navigate = useNavigate();

  const filteredDetections = useMemo(() => {
    return filterVeralliaDashboardDetections(detections, filters);
  }, [detections, filters]);

  const classification = useMemo(() => {
    return getVeralliaDashboardDefectClassification(filteredDetections);
  }, [filteredDetections]);

  const byHour = useMemo(() => {
    return getVeralliaDashboardByHour(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byDay = useMemo(() => {
    return getVeralliaDashboardByDay(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byWeek = useMemo(() => {
    return getVeralliaDashboardByWeek(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byMonth = useMemo(() => {
    return getVeralliaDashboardByMonth(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byShift = useMemo(() => {
    return getVeralliaDashboardByShift(filteredDetections, filters.start, filters.end);
  }, [filteredDetections, filters.start, filters.end]);

  const byLine = useMemo(() => {
    return getVeralliaDashboardByLine(filteredDetections);
  }, [filteredDetections]);

  const handleBarClick = (category, index, labels) => {
    const latest = filteredDetections.reduce((selected, detection) => {
      if (!matchesStatisticsBar(detection, category, index, labels[index])) return selected;
      return !selected || detection.timestamp > selected.timestamp ? detection : selected;
    }, null);
    if (!latest) return;

    const detectionIndex = filteredDetections.indexOf(latest);
    const groupId = groupVeralliaDashboardDetections([latest])[0].id;
    const targetGroupId = groupId.startsWith('single:') ? `single:${detectionIndex}` : groupId;
    const sortedGroups = sortVeralliaDashboardDetections(groupVeralliaDashboardDetections(filteredDetections));
    const targetIndex = sortedGroups.findIndex((group) => group.id === targetGroupId);
    if (targetIndex < 0) return;

    const pageStart = Math.floor(targetIndex / 25) * 25;
    const highlightIds = sortedGroups.slice(pageStart, pageStart + 25).filter((group) => matchesStatisticsBar(group, category, index, labels[index])).map((group) => group.id);
    if (!highlightIds.includes(targetGroupId)) highlightIds.push(targetGroupId);

    onDetectionsPageChange(Math.floor(targetIndex / 25) + 1);
    navigate('../detections', { relative: 'path', state: { veralliaStatisticsJump: { filters, groupId: targetGroupId, highlightIds } } });
  };

  if (isInfluxLoading) return <Loader/>;

  return (
    <Panel title="ESTADÍSTICAS">
      <div className="mx-auto flex w-11/12 flex-col gap-4 py-6">
        {hasInfluxError ? (
          <VeralliaDashboardNoData text="No se han podido cargar los datos."/>
        ) : (
          <>
            <VeralliaDashboardFilters detections={detections} value={filters} onChange={onFiltersChange}/>

            <div className="grid grid-cols-2 gap-4">
              <VeralliaDashboardKpi label="Detecciones en el período" value={filteredDetections.length}/>
              <VeralliaDashboardKpi label="Líneas con detecciones" value={getVeralliaDashboardLinesReporting(filteredDetections)}/>
            </div>

            <VeralliaDashboardDefectClassification classification={classification} fixedHeight/>

            <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
              <VeralliaDashboardBarChart title="Por hora" subtitle="Media de detecciones por hora" labels={byHour.labels} values={byHour.values} color={graphColors[0]} onBarClick={(index) => handleBarClick('hour', index, byHour.labels)}/>
              <VeralliaDashboardBarChart title="Por día" subtitle="Media de detecciones por día del mes" labels={byDay.labels} values={byDay.values} color={graphColors[1]} onBarClick={(index) => handleBarClick('day', index, byDay.labels)}/>
              <VeralliaDashboardBarChart title="Por semana" subtitle="Media de detecciones por semana ISO" labels={byWeek.labels} values={byWeek.values} color={graphColors[2]} onBarClick={(index) => handleBarClick('week', index, byWeek.labels)}/>
              <VeralliaDashboardBarChart title="Por mes" subtitle="Media de detecciones por mes" labels={byMonth.labels} values={byMonth.values} color={graphColors[3]} onBarClick={(index) => handleBarClick('month', index, byMonth.labels)}/>
              <VeralliaDashboardBarChart title="Por equipo" subtitle="Media de detecciones por equipo" labels={byShift.labels} values={byShift.values} color={graphColors[5]} onBarClick={(index) => handleBarClick('shift', index, byShift.labels)}/>
              <VeralliaDashboardBarChart title="Por línea" subtitle="Total de detecciones" labels={byLine.labels} values={byLine.values} color={graphColors[6]} onBarClick={(index) => handleBarClick('line', index, byLine.labels)}/>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
};
